import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Typography, Stack, Divider, Button } from '@mui/material';
import { Box } from '@mui/system';
import { CheckCircleRounded, UndoRounded } from '@mui/icons-material';
import { useTheme } from '@mui/system';
import { useUpdatePackItemReviewMutation, useLogUserActionMutation } from '../../../api/packSlice';
import { NumericFormat } from 'react-number-format';

function PackItemEntry({ itemReview, cardOwnerId, packReviewId, onClick }) {
    const theme = useTheme();
    const [isReviewed, setIsReviewed] = useState(itemReview.value !== null);
    const [updatePackItemReview, { isLoading }] = useUpdatePackItemReviewMutation();
    const [submitUserAction] = useLogUserActionMutation();

    const usageOptions = [
        { label: 'Never', fullLabel: 'Never (< 20%)', value: 1 },
        { label: 'Rarely', fullLabel: 'Rarely (20% - 50%)', value: 2 },
        { label: 'Often', fullLabel: 'Often (50% - 95%)', value: 3 },
        { label: 'Always', fullLabel: 'Always (> 95%)', value: 4 },
    ];

    const contextVariants = {
        open: {
            opacity: 1,
            height: 'auto',
            transition: { duration: 0.5, ease: 'easeInOut' },
        },
        collapsed: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: { duration: 0.5, ease: 'easeInOut' },
        },
    };

    const handleSelectUsage = async (usage) => {
        if (isLoading) return;
        try {
            await updatePackItemReview({
                cardOwnerId: cardOwnerId,
                packReviewId: packReviewId,
                packItemReviewId: itemReview.id,
                reviewData: { value: usage },
            }).unwrap();
            setIsReviewed(true);
            // Log item action
            submitUserAction({ action: 'item_review_action', data: { packReviewId, physicianId: cardOwnerId, packItemReviewId: itemReview.id, selectedUsage: usage } }).unwrap();
        } catch {
            // nothing to do, item will update from API
        }
    };

    const handleUnselectUsage = async () => {
        if (isLoading) return;
        try {
            await updatePackItemReview({
                cardOwnerId: cardOwnerId,
                packReviewId: packReviewId,
                packItemReviewId: itemReview.id,
                reviewData: { value: null },
            }).unwrap();
            setIsReviewed(false);
            submitUserAction({ action: 'item_review_action_undo', data: { packReviewId, physicianId: cardOwnerId, packItemReviewId: itemReview.id, selectedUsage: null } }).unwrap();
        } catch {
            // add error message here
        }
    };

    return (
        <Stack
            onClick={onClick}
            sx={{
                background: '#fff',
                borderRadius: 2,
                minWidth: 300,
            }}
        >
            {/* Item details and selection buttons (visible when NOT collapsed) */}
            <Box
                component={motion.div}
                initial={false}
                variants={contextVariants}
                animate={!isReviewed ? 'open' : 'collapsed'}
                transition={{ duration: 1.2, type: 'spring', bounce: 0.1 }}
                sx={{ width: '100%', overflow: 'hidden' }}
            >
                <Stack spacing={1} p={1.5}>
                    {/* Item Name */}
                    <Typography variant="heavy" fontSize='12px'>
                        {itemReview.name}
                    </Typography>
                    {/* Details Row */}
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Typography fontSize='12px' variant="body2" color="text.secondary">{itemReview.vendor_name}</Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography fontSize='12px' variant="body2" color="text.secondary">
                            <NumericFormat
                                value={itemReview.cost_per_uom}
                                displayType={'text'}
                                thousandSeparator={true}
                                prefix={'$'}
                                decimalScale={2}
                                fixedDecimalScale={false}
                            />
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography fontSize='12px' variant="body2" color="text.secondary">Qty: 
                            <NumericFormat
                                value={itemReview.quantity}
                                displayType={'text'}
                                thousandSeparator={true}
                                prefix={' '}
                                decimalScale={0}
                                fixedDecimalScale={true}
                            />
                        </Typography>
                    </Stack>
                    <Divider />
                    {/* Usage Question */}
                    <Typography variant="heavy" fontSize="12px">
                        How often do you use this pack item?
                    </Typography>
                    {/* Usage Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                        {usageOptions.map(opt => (
                            <Button
                                key={opt.value}
                                size='small'
                                variant={'outlined'}
                                sx={{ flex: 1, minWidth: 0, px: 2 }}
                                disabled={isLoading}
                                onClick={(e) => { e.stopPropagation(); handleSelectUsage(opt.value); }}
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </Stack>
                </Stack>
            </Box>

            {/* Feedback received, undo button (visible when 'collapsed') */}
            <Box
                component={motion.div}
                initial={false}
                variants={contextVariants}
                animate={isReviewed ? 'open' : 'collapsed'}
                transition={{ duration: 1.2, type: 'spring', bounce: 0.1 }}
                sx={{ width: '100%', overflow: 'hidden' }}
            >
                <Stack spacing={1} p={1.5}>
                    <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CheckCircleRounded fontSize='small' sx={{ color: `${theme.palette.green.taskText}`, mr: 1 }} />
                            <Typography variant="bold" color={`${theme.palette.text.primary}`}>Feedback Received</Typography>
                        </Stack>
                        <Typography variant="heavy" fontSize='14px'>{itemReview.name}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="normal" color={`${theme.palette.text.primary}`} fontSize="12px">Estimated Usage:</Typography>
                            <Typography variant="heavy" color={`${theme.palette.text.primary}`} fontSize="12px">{itemReview.value ? usageOptions.find(opt => opt.value === itemReview.value)?.fullLabel : ''}</Typography>
                        </Stack>
                        <Button size='small' variant="outlined" startIcon={<UndoRounded />} onClick={(e) => { e.stopPropagation(); handleUnselectUsage(); }} sx={{ ml: 'auto' }} disabled={isLoading}>Undo</Button>
                    </Stack>
                </Stack>
            </Box>
        </Stack>
    )
}

export default PackItemEntry;